import PiniaStore from "@/stores";
import { defineStore } from "pinia";
import { type Ref, ref, computed } from "vue";
import { JwtHelper } from "@/helpers/utilities/JwtHelper";
import apiSkobo from "@/helpers/api/apiSkobo";
import { useSettingsStore } from "@/stores/settings";
import { SessionHelper } from "@/helpers/utilities/sessionHelper";

interface Institution {
  institutionId: string;
  displayName: string;
  [key: string]: unknown;
}

export const useInstitutionsStore = defineStore("institutions", () => {
  const myInstitutionsState: Ref<Institution[]> = ref([]);
  const myActiveInstitutionIdState: Ref<string | null> = ref(null);

  const myInstitutions = computed(() => myInstitutionsState.value);
  const myActiveInstitutionId = computed(() => myActiveInstitutionIdState.value);
  const institution = computed(
    () => (institutionId: string) => myInstitutionsState.value.find(i => i.institutionId === institutionId)
  );
  const activeInstitution = computed(() =>
    myInstitutionsState.value.find(i => i.institutionId === myActiveInstitutionId.value)
  );

  const reloadMyInstitutions = async () => {
    try {
      const settingsStore = useSettingsStore();

      const response = await apiSkobo.get("/Institution/User/All");
      let institutions: Institution[] = response.data;
      institutions = institutions.sort((a, b) => a.displayName.localeCompare(b.displayName, settingsStore.language));
      myInstitutionsState.value = institutions;
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const loadMyInstitutions = async () => {
    if (myInstitutionsState.value.length === 0) {
      reloadMyInstitutions();
    }
  };

  const setActiveInstitutionFromToken = () => {
    myActiveInstitutionIdState.value = JwtHelper.getInstitutionId(SessionHelper.nctSessionToken());
  };

  const switchInstitution = async (newInstitutionId: string) => {
    const response = await apiSkobo.post(`/me/auth/swap/${newInstitutionId}`);
    SessionHelper.login(response.data);

    // Pre-fetch setup with new token before resetting store.
    // Resetting the store triggers a full App.vue remount via :key — doing the
    // API call first means the component tree is never destroyed mid-request.
    const setupResult = await apiSkobo.get("/me/setup");

    PiniaStore.resetStore();

    const settingsStore = useSettingsStore();
    await settingsStore.loadSetup(setupResult.data);

    setActiveInstitutionFromToken();
    await reloadMyInstitutions();
  };

  return {
    myInstitutionsState,
    myActiveInstitutionIdState,
    myInstitutions,
    myActiveInstitutionId,
    institution,
    activeInstitution,
    loadMyInstitutions,
    reloadMyInstitutions,
    switchInstitution,
    setActiveInstitutionFromToken
  };
});
